<template>
  <div>
    <AppModal v-model="showAddGroup" title="Create Group" hide-footer>
      <NewGroup @finished="onGroupCreated" />
        </AppModal>
    <AppModal v-model="showAddPermission" title="Create Permission" hide-footer>
      <NewPermission @finished="onPermissionCreated" />
        </AppModal>

    <!-- Header -->
    <div class="flex items-center justify-between py-3">
      <p class="text-sm text-gray-500">{{ groups.length }} permission group{{ groups.length !== 1 ? "s" : "" }}</p>
      <div class="flex gap-2">
        <button class="btn-primary" @click="openFormPermission">
          <i class="fa fa-plus mr-1"></i> Add Permission
        </button>
        <button class="btn-primary" @click="openFormGroup">
          <i class="fa fa-plus mr-1"></i> Add Group
        </button>
            </div>
    </div>

    <!-- Table -->
    <div>
      <table>
                <thead>
                <tr>
                  <th style="width: 2rem"></th>
                  <th>Group Name</th>
                  <th>Permissions</th>
                  <th>Action</th>
                </tr>
                </thead>
                <tbody>
                <!-- Skeleton -->
                <template v-if="loading">
                  <tr v-for="i in 5" :key="`sk-${i}`" class="animate-pulse">
                    <td>
                      <div class="h-3 bg-gray-200 rounded w-4"></div>
                    </td>
                    <td>
                      <div class="h-3 bg-gray-200 rounded w-40"></div>
                    </td>
                    <td>
                      <div class="h-3 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td>
                      <div class="h-3 bg-gray-200 rounded w-8"></div>
                    </td>
                  </tr>
                </template>

                <template v-else v-for="(group, idx) in groups" :key="idx">
                  <!-- Main row -->
                  <tr @click="toggleRow(idx)" class="cursor-pointer select-none">
                    <td>
                      <i class="fa text-gray-400 text-xs transition-transform duration-200"
                         :class="expandedRows.has(idx) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </td>
                    <td class="font-semibold font-mono text-xs">
                      {{ cleanUnderscoreString(group.name.replace("-", " ")) }}
                    </td>
                    <td>
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                {{
                                group.permissions?.length ?? 0
                              }} permission{{ (group.permissions?.length ?? 0) !== 1 ? "s" : "" }}
                            </span>
                    </td>
                    <td @click.stop>
                      <button class="btn-sm-primary">
                        <i class="fa fa-edit mr-1"></i> Edit
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded permissions row -->
                  <tr v-if="expandedRows.has(idx)">
                    <td colspan="4" class="bg-gray-50 px-4 py-3">
                      <div v-if="group.permissions?.length" class="flex flex-wrap gap-2">
                                <span
                                  v-for="(perm, pidx) in group.permissions"
                                  :key="pidx"
                                  class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-white border border-primary/20 text-primary-700"
                                >
                                    {{ perm }}
                                </span>
                      </div>
                      <p v-else class="text-xs text-gray-400 italic">No permissions in this group.</p>
                    </td>
                  </tr>
                </template>

                <tr v-if="!loading && groups.length === 0">
                  <td colspan="4" class="text-center py-6 text-gray-400 text-sm">No permission groups found</td>
                </tr>
                </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import NewGroup from "@/domains/home/services/accounts/components/roles/NewGroup.vue";
import NewPermission from "@/domains/home/services/accounts/components/roles/NewPermission.vue";
import { cleanUnderscoreString } from "@/commons/composables/transformations.ts";
import { useRolesStore } from "@/stores/roles";

interface Group {
  id: string;
  name: string;
  permissions: string[];
}

const rolesStore = useRolesStore();

const showAddGroup = ref(false)
const showAddPermission = ref(false)
const loading = ref(false)
const groups = ref<Group[]>([])
const expandedRows = ref<Set<number>>(new Set());

onMounted(() => {
  loading.value = true;
  rolesStore.fetchPermissionGroups();
})

const openFormPermission = () => {
  showAddPermission.value = true;
};
const openFormGroup = () => {
  showAddGroup.value = true;
};

const toggleRow = (idx: number) => {
  if (expandedRows.value.has(idx)) {
    expandedRows.value.delete(idx);
  } else {
    expandedRows.value.add(idx);
  }
}

const onGroupCreated = () => {
  showAddGroup.value = false;
};
const onPermissionCreated = () => {
  showAddPermission.value = false;
};

watch(() => rolesStore.permissionGroups, (data) => {
  loading.value = false;
  if (data?.data) {
    groups.value = Array.isArray(data.data) ? data.data : (data.data.content ?? []);
  } else if (Array.isArray(data)) {
    groups.value = data;
  }
})

watch(() => rolesStore.group, (data) => {
  if (data) showAddGroup.value = false;
})

watch(() => rolesStore.permission, (data) => {
  if (data) showAddPermission.value = false;
})
</script>
